import { useCallback, useState } from 'react';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import FoldersInterface from '../../../graphql/Folders/FoldersInterface';
import { mapFolderError } from '../../../graphql/Folders/errorMessages';

interface UseFolderLayoutActionsParams {
  id: string;
  initialName: string | undefined;
  onRename?: (folderId: string, newName: string) => void;
  onDelete?: (folderId: string) => void;
}

export function useFolderLayoutActions({
  id,
  initialName,
  onRename,
  onDelete,
}: UseFolderLayoutActionsParams) {
  const [localName, setLocalName] = useState(initialName);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const displayName = localName || initialName;

  const handleRenameClick = useCallback(() => setIsRenameDialogOpen(true), []);
  const handleRenameClose = useCallback(() => setIsRenameDialogOpen(false), []);

  const handleRenameSuccess = useCallback(
    (newName: string) => {
      setLocalName(newName);
      onRename?.(id, newName);
      setIsRenameDialogOpen(false);
    },
    [id, onRename]
  );

  const handleDeleteClick = useCallback(() => setIsDeleteDialogOpen(true), []);
  const handleDeleteClose = useCallback(() => setIsDeleteDialogOpen(false), []);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await new FoldersInterface().delete(id);
      onDelete?.(id);
      SnackbarStore.show('Folder deleted successfully');
      setIsDeleteDialogOpen(false);
    } catch (err: any) {
      SnackbarStore.show(mapFolderError(err));
    } finally {
      setIsDeleting(false);
    }
  }, [id, onDelete]);

  return {
    displayName,
    isRenameDialogOpen,
    isDeleteDialogOpen,
    isDeleting,
    handleRenameClick,
    handleRenameClose,
    handleRenameSuccess,
    handleDeleteClick,
    handleDeleteClose,
    handleDeleteConfirm,
  };
}
